// Scrolling and entrances on a work page. One instance per work page, created
// when the page mounts and disposed when it unmounts; the router never shows
// two pages at once, so there is no shared state to guard.
//
//   mouse/trackpad   smoothed wheel (glides toward a target), then a spring
//                    snap in the direction of the last push
//   click            lower half = next section, upper half = previous (eased tween)
//   touch            native scrolling with mandatory section snapping (a pull
//                    down at the very top is guarded: see touchMove)
//   keys, scrollbar  native
import { createEntrances, EASE, T } from "./entrance"
import { Arrival, Handoff, Sections } from "./sections"

export type ControllerOptions = {
    scrollTime: number // s, click scroll
    smooth: boolean
    lerp: number
    wheelSpeed: number
    snap: "soft" | "strict" | "off"
    replay: boolean
    trigger: number // % of a section on screen before it plays
    speed: number
    hideCursor: boolean
}

export const DEFAULTS: ControllerOptions = {
    scrollTime: 0.9,
    smooth: true,
    lerp: 0.1,
    wheelSpeed: 1.3,
    snap: "soft",
    replay: true,
    trigger: 30,
    speed: 1,
    hideCursor: true,
}

function bezier(x1: number, y1: number, x2: number, y2: number) {
    const bx = (t: number) => 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t
    const by = (t: number) => 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
    return (x: number) => {
        if (x <= 0) return 0
        if (x >= 1) return 1
        let lo = 0,
            hi = 1
        for (let i = 0; i < 24; i++) {
            const m = (lo + hi) / 2
            if (bx(m) < x) lo = m
            else hi = m
        }
        return by((lo + hi) / 2)
    }
}

/** The page cursor: black glass disc with an arrow (down = next, up = previous). */
/** the contact form is open over the page: it doesn't move under it */
const locked = () => document.documentElement.hasAttribute("data-contact")

function makeCursor() {
    // (the page grew: the cursor on screen carries on)
    const kept = document.querySelector<HTMLElement>("body > .w-cursor")
    if (kept) return { el: kept, arrow: kept.firstChild as SVGSVGElement }
    const el = document.createElement("div")
    el.setAttribute("aria-hidden", "true")
    el.className = "w-cursor"
    el.innerHTML =
        '<svg width="16" height="16" viewBox="-8 -8 16 16" fill="currentColor"><rect x="-7.5" y="0.5" width="3" height="3" rx="0.6"/><rect x="-1.5" y="-6" width="3" height="3" rx="0.6"/><rect x="4.5" y="0.5" width="3" height="3" rx="0.6"/></svg>'
    const arrow = el.firstChild as SVGSVGElement
    arrow.style.transform = "rotate(180deg)"
    arrow.style.transition = `transform ${T.arrow}s ${EASE.snap}, opacity .2s`
    document.body.appendChild(el)
    return { el, arrow }
}

/** keep: sections already on screen and in place (the page grew while they
 *  were shown): they're left as they are, entering or not, never primed again */
/** origin: the section the page opens at (the categories before it sit above) */
export function startWorkPage(list: HTMLElement[], opt: ControllerOptions = DEFAULTS, keep?: Set<HTMLElement>, origin?: HTMLElement) {
    const html = document.documentElement
    const scroller = (document.scrollingElement || html) as HTMLElement
    const reduced = matchMedia("(prefers-reduced-motion: reduce)")
    const fine = matchMedia("(hover: hover) and (pointer: fine)")
    const engine = createEntrances(opt.speed)
    const ease = bezier(0.8, 0, 0.2, 1) // Jitter "Natural"
    const cursor = makeCursor()
    // (restored to the defaults when the page is left: the site sets them nowhere else)
    const prev = { snap: "", cursor: "", pad: "" }
    let leaving = false
    let dir = 1 // cursor half: 1 = next, -1 = previous

    if (opt.hideCursor && fine.matches) html.style.cursor = "none"

    // ----- entrances -----
    // the opening section of every case enters like the page's first one
    const first = origin || list[0]
    const kindFor = (el: HTMLElement) => (el === first || el.matches("[data-case] > :first-child") ? "intro" : "slide")
    const visibleRatio = (el: HTMLElement) => {
        const r = el.getBoundingClientRect()
        const v = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0))
        return r.height > 0 ? v / Math.min(r.height, innerHeight) : 0
    }
    // Arriving through the page transition (or opening at the top): the covering
    // layer lifts as the first section plays. Opened further down: lift it now.
    const handoff = Handoff.take()
    let landed = !(handoff || (first && Math.abs(first.getBoundingClientRect().top) < 10 && visibleRatio(first) >= 0.3))
    if (landed) Handoff.lift(handoff)

    // Sections play from their real position on screen: on every scroll frame and
    // every 250ms, so one can never stay hidden while on screen. A section is
    // primed again (for replay) once it is fully off screen.
    const check = () => {
        if (leaving || reduced.matches) return
        const thr = Math.min(0.95, Math.max(0.05, opt.trigger / 100))
        const vh = innerHeight
        for (const el of list) {
            const r = el.getBoundingClientRect()
            const st = engine.state(el)
            if (r.bottom <= 1 || r.top >= vh - 1) {
                if (!st.primed && (opt.replay || !st.played)) engine.prime(el, kindFor(el), r.top < 0 ? -1 : 1)
            } else if (st.primed && visibleRatio(el) >= thr) {
                engine.play(el)
                if (!landed && el === first) {
                    landed = true
                    Handoff.lift(handoff)
                }
            }
        }
    }
    let checkQueued = 0
    const queueCheck = () => {
        if (!checkQueued)
            checkQueued = requestAnimationFrame(() => {
                checkQueued = 0
                check()
            })
    }
    list.forEach((el) => {
        el.style.position ||= "relative"
        el.style.zIndex ||= "1" // above the fixed background layer
    })
    if (!reduced.matches) {
        for (const el of list) {
            if (keep?.has(el)) continue
            const r = el.getBoundingClientRect()
            engine.prime(el, kindFor(el), r.top < 0 ? -1 : 1)
        }
        check()
    } else if (!landed) {
        landed = true
        Handoff.lift(handoff)
    }
    const checkTimer = setInterval(check, 250)
    // never leave the covering layer up if the first section somehow doesn't play
    const liftTimer = window.setTimeout(() => {
        if (!landed) {
            landed = true
            Handoff.lift(handoff)
        }
    }, 1800)

    // ----- scrolling -----
    const useSmooth = opt.smooth && fine.matches && !reduced.matches
    // CSS snapping can't work with a smoothed wheel (snapped in JS then). Touch:
    // native mandatory snapping, one section per swipe, and mobile Safari can
    // still collapse its URL bar.
    const cssSnap = useSmooth ? "" : !fine.matches ? "y mandatory" : opt.snap === "strict" ? "y mandatory" : opt.snap === "off" ? "" : "y proximity"
    html.style.scrollSnapType = cssSnap
    // No rubber band with mouse/trackpad: the page glides and springs itself
    // (restored when the page is left). Touch keeps the native bounce and
    // pull-to-refresh; the snapping waits out a pull at the top (touchEnd).
    const overscroll = html.style.overscrollBehaviorY
    if (useSmooth) html.style.overscrollBehaviorY = "none"
    // Touch: room below the last section, so snapping stops at its top and mobile
    // Safari doesn't bring its toolbar back there.
    if (!fine.matches) document.body.style.paddingBottom = "96px"

    let mode: "idle" | "lerp" | "tween" | "spring" = "idle"
    let raf = 0,
        last = 0,
        sv = 0, // scroll velocity (px/ms), carried into the snap spring
        y = scroller.scrollTop,
        ty = y,
        target = -1,
        snapTimer = 0,
        idleTimer = 0,
        touchLast = 0,
        wheelDir = 0
    let tween: { from: number; to: number; t0: number; dur: number } | null = null
    const maxY = () => Math.max(0, scroller.scrollHeight - innerHeight)
    const clampY = (v: number) => Math.max(0, Math.min(maxY(), v))
    const topOf = (el: HTMLElement) => el.getBoundingClientRect().top + scroller.scrollTop
    const setY = (v: number) => {
        y = v
        scroller.scrollTop = v
    }
    const restoreSnap = () => {
        clearTimeout(snapTimer)
        snapTimer = window.setTimeout(() => (html.style.scrollSnapType = cssSnap), 60)
    }
    const currentIndex = () => {
        if (target >= 0) return target
        let best = 0,
            dist = Infinity
        list.forEach((el, i) => {
            const d = Math.abs(el.getBoundingClientRect().top)
            if (d < dist) ((dist = d), (best = i))
        })
        return best
    }
    const paintArrow = () => {
        const i = currentIndex()
        const can = dir > 0 ? i < list.length - 1 : i > 0
        cursor.arrow.style.transform = dir > 0 ? "rotate(180deg)" : "rotate(0deg)"
        cursor.arrow.style.opacity = can ? "1" : "0.35"
    }
    const loop = (now: number) => {
        const dt = last ? Math.min(64, now - last) : 16.67
        last = now
        if (mode === "tween" && tween) {
            const p = Math.min(1, (now - tween.t0) / tween.dur)
            setY(tween.from + (tween.to - tween.from) * ease(p))
            if (p >= 1) {
                ty = tween.to
                tween = null
                target = -1
                mode = "idle"
            }
        } else if (mode === "spring") {
            // critically damped: soft start, soft landing, no overshoot
            const st = dt / 1000
            const k = 42,
                c = 2 * Math.sqrt(k)
            let v = sv * 1000
            v += (-k * (y - ty) - c * v) * st
            const ny = y + v * st
            sv = v / 1000
            if (Math.abs(ty - ny) < 0.5 && Math.abs(v) < 6) {
                setY(ty)
                mode = "idle"
            } else setY(ny)
        } else if (mode === "lerp") {
            const f = 1 - Math.pow(1 - Math.min(0.9, Math.max(0.01, opt.lerp)), dt / 16.67)
            const ny = y + (ty - y) * f
            sv = (ny - y) / dt
            if (Math.abs(ty - ny) < 0.5) {
                setY(ty)
                mode = "idle"
            } else setY(ny)
        }
        if (mode !== "idle") raf = requestAnimationFrame(loop)
        else {
            raf = 0
            last = 0
            restoreSnap()
            paintArrow()
        }
    }
    const start = () => {
        if (!raf) {
            last = 0
            raf = requestAnimationFrame(loop)
        }
    }
    const stop = () => {
        cancelAnimationFrame(raf)
        raf = 0
        last = 0
        mode = "idle"
        tween = null
        target = -1
        y = ty = scroller.scrollTop
        restoreSnap()
    }
    const scrollToIndex = (i: number) => {
        if (!list.length) return
        i = Math.max(0, Math.min(list.length - 1, i))
        const to = clampY(topOf(list[i]))
        if (reduced.matches) {
            scroller.scrollTop = y = ty = to
            return
        }
        const chained = mode === "tween" // fast clicks keep moving from where it is
        clearTimeout(snapTimer)
        clearTimeout(idleTimer)
        html.style.scrollSnapType = "none"
        y = scroller.scrollTop
        tween = { from: y, to, t0: performance.now(), dur: Math.max(120, opt.scrollTime * 1000 * (chained ? 0.75 : 1)) }
        target = i
        mode = "tween"
        start()
        paintArrow()
    }
    // Snapping follows the direction of the last push: a push past 12% of the
    // screen goes on to the next section, a push back returns. Waits until the
    // wheel has been still for a moment.
    const snapAfterWheel = () => {
        if (leaving || opt.snap === "off" || mode === "tween" || !list.length) return
        const tops = list.map((el) => clampY(topOf(el)))
        let cur = 0
        for (let i = 0; i < tops.length; i++) if (tops[i] <= ty + 1) cur = i
        const next = Math.min(tops.length - 1, cur + 1)
        const off = ty - tops[cur],
            span = tops[next] - tops[cur]
        if (off < 1 || span <= 0) return
        const push = innerHeight * 0.12
        let to = tops[cur]
        if (wheelDir > 0) to = off > push ? tops[next] : tops[cur]
        else if (wheelDir < 0) to = span - off > push ? tops[cur] : tops[next]
        else to = off > span / 2 ? tops[next] : tops[cur]
        if (opt.snap !== "strict" && Math.min(off, span - off) > innerHeight * 0.5) return
        ty = to
        mode = "spring"
        start()
    }

    // ----- arrival: hold the top while the page lands -----
    // (until 2s have passed or the visitor takes over)
    let arriving = !!Arrival.requested && Date.now() - Arrival.requested < 5000
    let arriveFrame = 0
    const arriveStart = performance.now()
    const prevAnchor = html.style.overflowAnchor
    const finishArrival = () => {
        if (!arriving) return
        arriving = false
        cancelAnimationFrame(arriveFrame)
        Arrival.requested = 0
        if (!leaving) {
            html.style.scrollSnapType = cssSnap
            html.style.overflowAnchor = prevAnchor
        }
    }
    const pinArrival = () => {
        if (!arriving) return
        if (leaving) return finishArrival()
        html.style.scrollSnapType = "none"
        html.style.overflowAnchor = "none"
        mode = "idle"
        tween = null
        sv = 0
        target = -1
        y = ty = first ? Math.max(0, first.getBoundingClientRect().top + scroller.scrollTop) : 0
        scroller.scrollTop = y
        if (performance.now() - arriveStart < 2000) arriveFrame = requestAnimationFrame(pinArrival)
        else finishArrival()
    }

    // A wheel (trackpad) gesture still running when the page opens — the push
    // that brought you here and its momentum — is not for this page: it's held
    // until the wheel has been still for a moment.
    let carried = true,
        lastAny = performance.now()

    // ----- input -----
    const wheel = (e: WheelEvent) => {
        if (locked()) return
        if (e.ctrlKey) return // pinch zoom
        if (leaving) {
            if (e.cancelable) e.preventDefault() // hold still under the exit
            return
        }
        const now = performance.now()
        if (carried) {
            const still = now - lastAny > 200
            lastAny = now
            if (!still) {
                if (e.cancelable) e.preventDefault()
                return
            }
            carried = false
        }
        if (!useSmooth) {
            if (mode !== "idle") stop()
            return
        }
        e.preventDefault()
        if (mode === "tween") {
            tween = null
            target = -1
            mode = "idle"
        }
        if (mode === "idle") y = ty = scroller.scrollTop
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1
        ty = clampY(ty + e.deltaY * unit * opt.wheelSpeed)
        if (e.deltaY) wheelDir = Math.sign(e.deltaY)
        clearTimeout(snapTimer)
        html.style.scrollSnapType = "none"
        mode = "lerp"
        start()
        clearTimeout(idleTimer)
        idleTimer = window.setTimeout(snapAfterWheel, 260)
    }
    const pointer = (e: PointerEvent) => {
        if (e.pointerType !== "mouse" || leaving) return
        // over the navigation or a video's sound button: native cursor, no page arrow
        // (and over the contact form: the page is locked under it)
        if ((e.target as Element | null)?.closest?.(".nto27, .sh, .ct-overlay, .w-sound") || locked()) {
            cursor.el.style.opacity = "0"
            html.style.cursor = ""
            return
        }
        if (opt.hideCursor && fine.matches) html.style.cursor = "none"
        cursor.el.style.transform = `translate3d(${e.clientX - 30}px, ${e.clientY - 30}px, 0)`
        cursor.el.style.opacity = "1"
        const next = e.clientY < innerHeight / 2 ? -1 : 1
        if (next !== dir) {
            dir = next
            paintArrow() // flips the moment the cursor crosses the middle
        }
    }
    const pointerOut = (e: PointerEvent) => {
        if (!e.relatedTarget) cursor.el.style.opacity = "0"
    }
    const click = (e: MouseEvent) => {
        if (leaving || performance.now() - touchLast < 600) return
        if (locked() || (e.target as HTMLElement)?.closest?.("a,button,input,textarea,select,.nto27,.sh,.ct-overlay")) return
        scrollToIndex(currentIndex() + (e.clientY < innerHeight / 2 ? -1 : 1))
    }
    const onScroll = () => {
        if (leaving) return
        queueCheck()
        if (mode === "idle") {
            y = ty = scroller.scrollTop // keys, scrollbar, touch
            paintArrow()
        }
    }
    // ----- touch: the pull at the top -----
    // Scrolling stays the browser's own (follows the finger, momentum, snapping).
    // One exception: a pull down at the very top makes iPhone bounce, and the
    // snapping took that bounce back for a swipe and moved on to the next
    // section by itself. So the moment a touch that started at the top pulls
    // down, snapping is paused; once the bounce has settled the page is put back
    // at the top and snapping resumes. Swipes up are never touched, and the
    // bounce and pull-to-refresh stay native.
    let pull: null | { y0: number; guard: boolean } = null,
        pullTimer = 0
    const touchStart = (e: TouchEvent) => {
        if (mode !== "idle") stop() // the finger takes over
        const t = e.touches[0]
        pull = cssSnap && t && e.touches.length === 1 && scroller.scrollTop <= 1 ? { y0: t.clientY, guard: false } : null
    }
    const touchMove = (e: TouchEvent) => {
        if (!pull || pull.guard) return
        const t = e.touches[0]
        if (t && t.clientY - pull.y0 > 6) {
            pull.guard = true
            clearTimeout(snapTimer)
            clearTimeout(pullTimer)
            html.style.scrollSnapType = "none"
        }
    }
    const touchEnd = () => {
        touchLast = performance.now()
        const p = pull
        pull = null
        if (!p?.guard) return
        let lastY = NaN
        const rest = () => {
            if (leaving) return
            const yy = scroller.scrollTop
            if (yy !== lastY) return ((lastY = yy), void (pullTimer = window.setTimeout(rest, 100)))
            if (yy > 0 && yy < innerHeight * 0.5) scroller.scrollTop = 0
            html.style.scrollSnapType = cssSnap
        }
        pullTimer = window.setTimeout(rest, 100)
    }
    const key = (e: KeyboardEvent) => {
        if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "Home", "End"].includes(e.key) && mode !== "idle") stop()
    }

    // the section indicator scrolls with the same tween as a click
    Sections.goTo = (el: HTMLElement) => {
        const i = list.indexOf(el)
        if (i >= 0 && !leaving) scrollToIndex(i)
        return i >= 0
    }
    // the page is being left: stop gliding and snapping, leave the scroll alone
    Sections.leave = () => {
        leaving = true
        Sections.leaving = true
        cancelAnimationFrame(raf)
        raf = 0
        mode = "idle"
        tween = null
        clearTimeout(idleTimer)
        clearTimeout(snapTimer)
        cursor.el.style.opacity = "0"
    }

    const on: [EventTarget, string, any, any?][] = [
        [window, "wheel", finishArrival, { passive: true, capture: true }],
        [window, "touchstart", finishArrival, { passive: true, capture: true }],
        [window, "keydown", finishArrival, true],
        [window, "pointerdown", finishArrival, true],
        [window, "pointermove", pointer],
        [window, "pointerout", pointerOut],
        [window, "click", click],
        [window, "wheel", wheel, { passive: false }],
        [window, "keydown", key],
        [window, "touchstart", touchStart, { passive: true }],
        [window, "touchmove", touchMove, { passive: true }],
        [window, "touchend", touchEnd, { passive: true }],
        [window, "touchcancel", touchEnd, { passive: true }],
        [window, "scroll", onScroll, { passive: true }],
    ]
    on.forEach(([t, n, f, o]) => t.addEventListener(n, f, o))
    pinArrival()
    paintArrow()

    /** keep: sections staying on screen while the page grows (their entrances go on) */
    const dispose = function dispose(keep?: Set<HTMLElement>) {
        finishArrival()
        on.forEach(([t, n, f, o]) => t.removeEventListener(n, f, o))
        clearInterval(checkTimer)
        html.style.overscrollBehaviorY = overscroll
        clearTimeout(liftTimer)
        cancelAnimationFrame(checkQueued)
        cancelAnimationFrame(raf)
        clearTimeout(snapTimer)
        clearTimeout(idleTimer)
        clearTimeout(pullTimer)
        if (!keep) cursor.el.remove()
        engine.dispose(keep)
        html.style.scrollSnapType = prev.snap
        html.style.cursor = prev.cursor
        document.body.style.paddingBottom = prev.pad
        Sections.goTo = null
        Sections.leave = null
    }
    /** more sections at the end of the page (the feed grew): set up and
     *  waiting for their entrances, like the ones there from the start */
    const add = (more: HTMLElement[]) => {
        for (const el of more) {
            if (list.includes(el)) continue
            el.style.position ||= "relative"
            el.style.zIndex ||= "1"
            list.push(el)
            if (!reduced.matches) {
                const r = el.getBoundingClientRect()
                if (r.bottom <= 1 || r.top >= innerHeight - 1) engine.prime(el, kindFor(el), r.top < 0 ? -1 : 1)
            }
        }
        paintArrow()
    }
    return Object.assign(dispose, { add })
}
